import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Shield, Image, Trash2, Search, ChevronLeft,
  Crown, Check, X, Upload, AlertTriangle, BookOpen, Gavel, Megaphone, Flag, HeartHandshake,
  DollarSign, Loader2, Bell, Send, CalendarClock, Plus, ToggleLeft, ToggleRight, ImagePlus, Moon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';
import AdManager from './AdManager';
import EventsAdmin from './EventsAdmin';
import LibraryAdmin from './LibraryAdmin';
import { CalendarDays, Library as LibraryIcon } from 'lucide-react';
import ReportsQueue from './ReportsQueue';

const AdminPage = ({ onBack, onNavigate }) => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [brotherAvatars, setBrotherAvatars] = useState([]);
  const [sisterAvatars, setSisterAvatars] = useState([]);
  const [avatarGenderFilter, setAvatarGenderFilter] = useState('brother');
  const [newAvatarName, setNewAvatarName] = useState('');
  const [newAvatarGender, setNewAvatarGender] = useState('brother');
  const [newAvatarFile, setNewAvatarFile] = useState(null);
  const [newAvatarPreview, setNewAvatarPreview] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [avatarsLoading, setAvatarsLoading] = useState(false);
  const [avatarTableMissing, setAvatarTableMissing] = useState(false);
  // Broadcast tab
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);

  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) return;
    setBroadcastSending(true);
    setBroadcastResult(null);
    try {
      const res = await api.broadcastNotification(broadcastMsg.trim());
      setBroadcastResult({ ok: true, text: `Sent to ${res.data.sent} members.` });
      setBroadcastMsg('');
    } catch (err) {
      setBroadcastResult({ ok: false, text: err?.response?.data?.detail || 'Failed to send.' });
    } finally {
      setBroadcastSending(false);
    }
  };

  // Seed Posts tab
  const [seedQueue, setSeedQueue] = useState([]);
  const [seedQueueLoading, setSeedQueueLoading] = useState(false);
  const [seedAiEnabled, setSeedAiEnabled] = useState(false);
  const [seedAiToggling, setSeedAiToggling] = useState(false);
  const [mentorSignupEnabled, setMentorSignupEnabled] = useState(null);
  const [mentorToggling, setMentorToggling] = useState(false);
  const [mentorToggleError, setMentorToggleError] = useState('');
  const [seedRows, setSeedRows] = useState([{ user_id: '', content: '', scheduled_for: '', imageFile: null, imagePreview: null }]);
  const MAX_SEED_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB
  const [seedAccounts, setSeedAccounts] = useState([]);
  const [seedCap, setSeedCap] = useState(7);
  const [seedSubmitting, setSeedSubmitting] = useState(false);
  const [seedMsg, setSeedMsg] = useState({ type: '', text: '' });
  const [seedQueueTab, setSeedQueueTab] = useState('pending');

  // Islamojis tab
  const [islamojiList, setIslamojiList] = useState([]);
  const [islamojiLoading, setIslamojiLoading] = useState(false);
  const [islamojiFilterTab, setIslamojiFilterTab] = useState('people');
  const [islamojiName, setIslamojiName] = useState('');
  const [islamojiSortOrder, setIslamojiSortOrder] = useState('0');
  const [islamojiType, setIslamojiType] = useState('emoji');
  const [islamojiCategory, setIslamojiCategory] = useState('people');
  const [islamojiFile, setIslamojiFile] = useState(null);
  const [islamojiUploading, setIslamojiUploading] = useState(false);
  const [islamojiMsg, setIslamojiMsg] = useState({ type: '', text: '' });
  const ISLAMOJI_CATEGORIES = ['people', 'food', 'things', 'places', 'sayings'];

  // Payouts tab
  const [payouts, setPayouts] = useState(null);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsError, setPayoutsError] = useState('');
  const [payoutMonth, setPayoutMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchUsers();
    fetchAvatars();
  }, []);

  const fetchPayouts = useCallback(async (month) => {
    setPayoutsLoading(true);
    setPayoutsError('');
    try {
      const res = await api.getMentorPayouts(month);
      setPayouts(res.data);
    } catch (err) {
      setPayoutsError(err?.response?.data?.detail || 'Failed to load payout report.');
    } finally {
      setPayoutsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'payouts') fetchPayouts(payoutMonth);
  }, [activeTab, payoutMonth, fetchPayouts]);

  useEffect(() => {
    if (activeTab !== 'payouts') return;
    api.getMentorSignupSetting()
      .then((res) => setMentorSignupEnabled(!!res.data?.enabled))
      .catch(() => setMentorSignupEnabled(false));
  }, [activeTab]);

  const handleToggleMentorSignup = async () => {
    setMentorToggling(true);
    setMentorToggleError('');
    try {
      const res = await api.setAdminSetting('mentor_signup_enabled', !mentorSignupEnabled);
      setMentorSignupEnabled(!!res.data?.value);
    } catch {
      setMentorToggleError('Could not update the setting. Please try again.');
    } finally {
      setMentorToggling(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'seedposts') return;
    setSeedQueueLoading(true);
    Promise.all([api.getSeedQueue(), api.getSeedAiEnabled(), api.getSeedAuthors()])
      .then(([qRes, aiRes, authorsRes]) => {
        setSeedQueue(qRes.data || []);
        setSeedAiEnabled(aiRes.data?.enabled ?? false);
        setSeedAccounts(authorsRes.data?.authors || []);
        setSeedCap(authorsRes.data?.cap || 7);
      })
      .catch(() => {})
      .finally(() => setSeedQueueLoading(false));
  }, [activeTab]);

  const loadIslamojis = useCallback(() => {
    setIslamojiLoading(true);
    api.getIslamojis()
      .then(res => setIslamojiList(res.data || []))
      .catch(() => {})
      .finally(() => setIslamojiLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'islamojis') loadIslamojis();
  }, [activeTab, loadIslamojis]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = users.filter(user => 
        user.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.getAllUsers();
      setUsers(res.data || []);
      setFilteredUsers(res.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setMessage({ type: 'error', text: 'Failed to fetch users' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAvatars = async () => {
    setAvatarsLoading(true);
    setAvatarTableMissing(false);
    try {
      const [brotherRes, sisterRes] = await Promise.all([
        api.getAvatars('brother'),
        api.getAvatars('sister'),
      ]);
      setBrotherAvatars(brotherRes.data || []);
      setSisterAvatars(sisterRes.data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.includes('avatars') && (detail.includes('does not exist') || detail.includes('relation'))) {
        setAvatarTableMissing(true);
      } else {
        console.error('Error fetching avatars:', err);
      }
    } finally {
      setAvatarsLoading(false);
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    setActionLoading(userId);
    try {
      await api.updateUser(userId, { is_admin: !currentStatus });
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, is_admin: !currentStatus } : u
      ));
      setMessage({ type: 'success', text: `Admin status ${!currentStatus ? 'granted' : 'revoked'}` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update admin status' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleMentor = async (userId, currentStatus) => {
    setActionLoading(userId);
    try {
      await api.updateUser(userId, { is_mentor: !currentStatus });
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, is_mentor: !currentStatus } : u
      ));
      setMessage({ type: 'success', text: `Mentor status ${!currentStatus ? 'granted' : 'revoked'}` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update mentor status' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleModerator = async (userId, currentStatus) => {
    setActionLoading(userId);
    try {
      await api.updateUser(userId, { is_moderator: !currentStatus });
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, is_moderator: !currentStatus } : u
      ));
      setMessage({ type: 'success', text: `Moderator status ${!currentStatus ? 'granted' : 'revoked'}` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update moderator status' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, displayName) => {
    if (!window.confirm(`Are you sure you want to delete ${displayName}? This action cannot be undone.`)) return;
    setActionLoading(userId);
    try {
      await api.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setMessage({ type: 'success', text: 'User deleted successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete user' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      setMessage({ type: 'error', text: 'Please select a JPEG, PNG, WebP, GIF, or SVG image.' });
      return;
    }
    setNewAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setNewAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearFileSelection = () => {
    setNewAvatarFile(null);
    setNewAvatarPreview(null);
  };

  const handleAddAvatar = async () => {
    if (!newAvatarFile || !newAvatarName.trim()) {
      setMessage({ type: 'error', text: 'Please enter a name and select an image file' });
      return;
    }
    setActionLoading('add-avatar');
    try {
      const formData = new FormData();
      formData.append('file', newAvatarFile);
      formData.append('name', newAvatarName.trim());
      formData.append('gender', newAvatarGender);
      await api.uploadAvatar(formData);
      setNewAvatarName('');
      setNewAvatarFile(null);
      setNewAvatarPreview(null);
      await fetchAvatars();
      setMessage({ type: 'success', text: `Avatar added for ${newAvatarGender === 'brother' ? 'Brothers' : 'Sisters'}` });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to upload avatar.';
      setMessage({ type: 'error', text: detail });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveAvatar = async (avatarId) => {
    setActionLoading(avatarId);
    try {
      await api.deleteAvatar(avatarId);
      await fetchAvatars();
      setMessage({ type: 'success', text: 'Avatar removed' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove avatar' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSeedAvatars = async () => {
    setActionLoading('seed');
    try {
      await api.seedAvatars();
      await fetchAvatars();
      setAvatarTableMissing(false);
      setMessage({ type: 'success', text: 'Default avatars seeded successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to seed avatars. Make sure the avatars table exists in Supabase.' });
    } finally {
      setActionLoading(null);
    }
  };

  // Deny non-admin
  if (!profile?.is_admin) {
    return (
      <div className="max-w-3xl mx-auto p-4 lg:p-8">
        <div className="glass-card rounded-arch-xl p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-semibold text-text-main mb-2">Access Denied</h2>
          <p className="text-text-muted mb-6">You don't have admin permissions to access this dashboard.</p>
          <button onClick={onBack} className="px-6 py-2 bg-primary text-white rounded-arch font-medium" data-testid="admin-go-back-btn">Go Back</button>
        </div>
      </div>
    );
  }

  const displayedAvatars = avatarGenderFilter === 'brother' ? brotherAvatars : sisterAvatars;

  const handleIslamojiUpload = async () => {
    if (!islamojiName.trim() || !islamojiFile) {
      setIslamojiMsg({ type: 'error', text: 'Add a name and choose a file.' });
      return;
    }
    setIslamojiUploading(true);
    setIslamojiMsg({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('file', islamojiFile);
      formData.append('name', islamojiName.trim());
      formData.append('type', islamojiType);
      if (islamojiType === 'emoji') formData.append('category', islamojiCategory);
      formData.append('sort_order', islamojiSortOrder || '0');
      await api.uploadIslamoji(formData);
      setIslamojiMsg({ type: 'success', text: 'Islamoji added!' });
      setIslamojiName('');
      setIslamojiFile(null);
      loadIslamojis();
    } catch (err) {
      setIslamojiMsg({ type: 'error', text: err?.response?.data?.detail || 'Failed to upload.' });
    } finally {
      setIslamojiUploading(false);
    }
  };

  const handleIslamojiDelete = async (id) => {
    try {
      await api.deleteIslamoji(id);
      setIslamojiList(list => list.filter(x => x.id !== id));
    } catch { }
  };

  const islamojiGifs = islamojiList.filter(i => i.type === 'gif');
  const islamojiEmojisInTab = islamojiList.filter(i => i.type === 'emoji' && i.category === islamojiFilterTab);

  return (
    <div className="max-w-5xl mx-auto p-4 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-arch-xl overflow-hidden" data-testid="admin-dashboard">
        {/* Header */}
        <div className="p-6 border-b border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={onBack} className="p-2 hover:bg-primary/10 rounded-full transition-colors" data-testid="admin-back-btn">
                <ChevronLeft className="w-6 h-6 text-primary" />
              </button>
              <h1 className="font-heading text-2xl font-semibold text-primary flex items-center gap-2">
                <Shield className="w-6 h-6" />
                Admin Dashboard
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => onNavigate?.('course-builder')}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 flex items-center gap-2 transition-colors"
                data-testid="admin-course-builder-btn">
                <BookOpen className="w-4 h-4" /> Course Builder
              </button>
              <button onClick={() => onNavigate?.('wallpaper-manager')}
                className="px-4 py-2 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 flex items-center gap-2 transition-colors"
                data-testid="admin-wallpaper-manager-btn">
                Wallpapers
              </button>
            </div>
          </div>
        </div>

        {/* Message */}
        <AnimatePresence>
          {message.text && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className={`mx-6 mt-4 px-4 py-3 rounded-arch flex items-center gap-2 ${message.type === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
              {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
              {message.text}
              <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto"><X className="w-4 h-4" /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="border-b border-white/20">
        <div className="flex">
          <button onClick={() => setActiveTab('users')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'users' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}
            data-testid="admin-tab-users">
            <Users className="w-5 h-5" /> Manage Users
          </button>
          <button onClick={() => setActiveTab('avatars')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'avatars' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}
            data-testid="admin-tab-avatars">
            <Image className="w-5 h-5" /> Avatar Library
          </button>
          <button onClick={() => setActiveTab('ads')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'ads' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}
            data-testid="admin-tab-ads">
            <Megaphone className="w-5 h-5" /> Ads
          </button>
          <button onClick={() => setActiveTab('reports')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'reports' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}
            data-testid="admin-tab-reports">
            <Flag className="w-5 h-5" /> Reports
          </button>
          <button onClick={() => setActiveTab('payouts')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'payouts' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}
            data-testid="admin-tab-payouts">
            <DollarSign className="w-5 h-5" /> Payouts
          </button>
          <button onClick={() => setActiveTab('broadcast')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'broadcast' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}
            data-testid="admin-tab-broadcast">
            <Bell className="w-5 h-5" /> Broadcast
          </button>
          <button onClick={() => setActiveTab('seedposts')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'seedposts' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}>
            <CalendarClock className="w-5 h-5" aria-hidden="true" /> Seed Posts
          </button>
        </div>
        <div className="flex border-t border-white/10">
          <button onClick={() => setActiveTab('islamojis')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'islamojis' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}
            data-testid="admin-tab-islamojis">
            <Moon className="w-5 h-5" aria-hidden="true" /> Islamojis
          </button>
          <button onClick={() => setActiveTab('events')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'events' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}
            data-testid="admin-tab-events">
            <CalendarDays className="w-5 h-5" aria-hidden="true" /> Events
          </button>
          <button onClick={() => setActiveTab('library')}
            className={`flex-1 px-6 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'library' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-muted hover:text-text-main'}`}
            data-testid="admin-tab-library">
            <LibraryIcon className="w-5 h-5" aria-hidden="true" /> Library
          </button>
        </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* ======== USERS TAB ======== */}
          {activeTab === 'users' && (
            <div>
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input type="text" placeholder="Search users by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/70 dark:bg-white dark:text-gray-800 rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary"
                  data-testid="admin-search-users" />
              </div>

              {loading ? (
                <div className="text-center py-8 text-text-muted">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-text-muted">No users found</div>
              ) : (
                <div className="space-y-3">
                  {filteredUsers.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-arch-lg" data-testid={`admin-user-${user.id}`}>
                      <div className="flex items-center gap-3">
                        <img src={user.avatar_url || defaultAvatar(user.gender)} alt={user.display_name} className="w-12 h-12 rounded-full object-cover border-2 border-primary/30" />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-text-main">{user.display_name}</p>
                            {user.is_admin && (
                              <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full flex items-center gap-1">
                                <Crown className="w-3 h-3" /> Admin
                              </span>
                            )}
                            {user.is_moderator && !user.is_admin && (
                              <span className="px-2 py-0.5 text-xs rounded-full flex items-center gap-1" style={{ background: 'rgba(169,156,200,0.2)', color: '#7d6dab' }}>
                                <Gavel className="w-3 h-3" /> Moderator
                              </span>
                            )}
                            {user.is_mentor && !user.is_admin && (
                              <span className="px-2 py-0.5 text-xs rounded-full flex items-center gap-1" style={{ background: 'rgba(234,179,8,0.15)', color: '#92680a' }}>
                                <HeartHandshake className="w-3 h-3" /> Mentor
                              </span>
                            )}
                            <span className={`px-2 py-0.5 text-xs rounded-full ${user.gender === 'sister' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                              {user.gender === 'sister' ? 'Sister' : 'Brother'}
                            </span>
                          </div>
                          <p className="text-sm text-text-muted">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleToggleMentor(user.id, user.is_mentor)} disabled={actionLoading === user.id || user.id === profile.id}
                          className="p-2 rounded-full transition-colors disabled:opacity-50"
                          style={user.is_mentor
                            ? { background: 'rgba(234,179,8,0.25)', color: '#92680a' }
                            : { background: '#f3f4f6', color: '#6b7280' }}
                          title={user.is_mentor ? 'Revoke Mentor' : 'Make Mentor'} data-testid={`mentor-toggle-${user.id}`}>
                          <HeartHandshake className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleToggleAdmin(user.id, user.is_admin)} disabled={actionLoading === user.id || user.id === profile.id}
                          className={`p-2 rounded-full transition-colors ${user.is_admin ? 'bg-primary/20 text-primary hover:bg-primary/30' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'} disabled:opacity-50`}
                          title={user.is_admin ? 'Revoke Admin' : 'Make Admin'} data-testid={`admin-toggle-${user.id}`}>
                          <Shield className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleToggleModerator(user.id, user.is_moderator)} disabled={actionLoading === user.id || user.id === profile.id}
                          className="p-2 rounded-full transition-colors disabled:opacity-50"
                          style={user.is_moderator
                            ? { background: 'rgba(169,156,200,0.25)', color: '#7d6dab' }
                            : { background: '#f3f4f6', color: '#6b7280' }}
                          title={user.is_moderator ? 'Revoke Moderator' : 'Make Moderator'} data-testid={`moderator-toggle-${user.id}`}>
                          <Gavel className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteUser(user.id, user.display_name)} disabled={actionLoading === user.id || user.id === profile.id}
                          className="p-2 bg-red-100 text-red-500 rounded-full hover:bg-red-200 transition-colors disabled:opacity-50"
                          title="Delete User" data-testid={`admin-delete-${user.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ======== AVATARS TAB ======== */}
          {activeTab === 'avatars' && (
            <div>
              {avatarTableMissing && (
                <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-arch-lg">
                  <h3 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Avatars Table Not Found
                  </h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400 mb-3">
                    The <code className="bg-yellow-100 dark:bg-yellow-900/40 px-1 rounded">avatars</code> table doesn't exist in your Supabase database yet. Please create it by running the SQL provided in the setup instructions, then click "Seed Default Avatars" below.
                  </p>
                  <button onClick={handleSeedAvatars} disabled={actionLoading === 'seed'}
                    className="px-4 py-2 bg-primary text-white rounded-arch font-medium disabled:opacity-50"
                    data-testid="seed-avatars-btn">
                    {actionLoading === 'seed' ? 'Seeding...' : 'Seed Default Avatars'}
                  </button>
                </div>
              )}

              {/* Add New Avatar */}
              <div className="mb-6 p-5 bg-white/30 dark:bg-white/5 rounded-arch-lg">
                <h3 className="font-medium text-text-main mb-3">Add New Avatar</h3>
                <p className="text-sm text-text-muted mb-4">
                  Upload an image from your computer and assign it to Brothers or Sisters.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <input type="text" placeholder="Avatar name (e.g. Aisha)" value={newAvatarName} onChange={e => setNewAvatarName(e.target.value)}
                    className="px-4 py-2.5 bg-white/70 dark:bg-white dark:text-gray-800 rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary"
                    data-testid="admin-avatar-name-input" />
                  <select value={newAvatarGender} onChange={e => setNewAvatarGender(e.target.value)}
                    className="px-4 py-2.5 bg-white/70 dark:bg-white dark:text-gray-800 rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary"
                    data-testid="admin-avatar-gender-select">
                    <option value="brother">For Brothers</option>
                    <option value="sister">For Sisters</option>
                  </select>
                </div>
                <div className="flex items-center gap-4">
                  {/* Image preview */}
                  {newAvatarPreview ? (
                    <div className="relative flex-shrink-0">
                      <img src={newAvatarPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-primary" />
                      <button onClick={clearFileSelection}
                        className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full"
                        data-testid="clear-avatar-preview">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex-shrink-0 w-20 h-20 rounded-full border-2 border-dashed border-primary/40 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                      data-testid="avatar-upload-area">
                      <Upload className="w-5 h-5 text-primary/60" />
                      <span className="text-[10px] text-primary/60 mt-0.5">Upload</span>
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml" onChange={handleFileSelect} className="hidden" data-testid="admin-avatar-file-input" />
                    </label>
                  )}
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-white/70 dark:bg-white/10 rounded-arch border border-primary/20 cursor-pointer hover:bg-primary/5 transition-colors">
                      <Upload className="w-4 h-4 text-primary" />
                      <span className="text-sm text-text-main">{newAvatarFile ? newAvatarFile.name : 'Choose image file...'}</span>
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml" onChange={handleFileSelect} className="hidden" />
                    </label>
                    <button onClick={handleAddAvatar} disabled={!newAvatarFile || !newAvatarName.trim() || actionLoading === 'add-avatar'}
                      className="px-5 py-2.5 bg-primary text-white rounded-arch font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="admin-add-avatar-btn">
                      <Upload className="w-4 h-4" />
                      {actionLoading === 'add-avatar' ? 'Uploading...' : 'Upload Avatar'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Gender filter tabs */}
              <div className="flex gap-2 mb-6">
                <button onClick={() => setAvatarGenderFilter('brother')}
                  className={`px-5 py-2 rounded-full font-medium text-sm transition-colors ${avatarGenderFilter === 'brother' ? 'bg-blue-100 text-blue-700 border-2 border-blue-300' : 'bg-white/30 dark:bg-white/5 text-text-muted hover:text-text-main border-2 border-transparent'}`}
                  data-testid="avatar-filter-brother">
                  Brothers ({brotherAvatars.length})
                </button>
                <button onClick={() => setAvatarGenderFilter('sister')}
                  className={`px-5 py-2 rounded-full font-medium text-sm transition-colors ${avatarGenderFilter === 'sister' ? 'bg-pink-100 text-pink-700 border-2 border-pink-300' : 'bg-white/30 dark:bg-white/5 text-text-muted hover:text-text-main border-2 border-transparent'}`}
                  data-testid="avatar-filter-sister">
                  Sisters ({sisterAvatars.length})
                </button>
              </div>

              {/* Seed button if no avatars */}
              {!avatarsLoading && !avatarTableMissing && displayedAvatars.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-text-muted mb-4">No {avatarGenderFilter === 'brother' ? 'brother' : 'sister'} avatars yet.</p>
                  <button onClick={handleSeedAvatars} disabled={actionLoading === 'seed'}
                    className="px-4 py-2 bg-primary text-white rounded-arch font-medium disabled:opacity-50"
                    data-testid="seed-avatars-btn-inline">
                    {actionLoading === 'seed' ? 'Seeding...' : 'Seed Default Avatars'}
                  </button>
                </div>
              )}

              {/* Avatar Grid */}
              {avatarsLoading ? (
                <div className="text-center py-8 text-text-muted">Loading avatars...</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                  {displayedAvatars.map((avatar) => (
                    <div key={avatar.id} className="relative group text-center">
                      <img src={avatar.url} alt={avatar.name}
                        className="w-full aspect-square rounded-full object-cover border-2 border-primary/20 group-hover:border-primary transition-colors" />
                      <p className="text-xs text-text-muted mt-1 truncate">{avatar.name}</p>
                      <button onClick={() => handleRemoveAvatar(avatar.id)} disabled={actionLoading === avatar.id}
                        className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                        title="Remove avatar" data-testid={`delete-avatar-${avatar.id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ======== ADS TAB ======== */}
          {activeTab === 'ads' && (
            <AdManager />
          )}

          {/* ======== EVENTS TAB ======== */}
          {activeTab === 'events' && (
            <EventsAdmin />
          )}

          {/* ======== LIBRARY TAB ======== */}
          {activeTab === 'library' && (
            <LibraryAdmin />
          )}

          {/* ======== REPORTS TAB ======== */}
          {activeTab === 'reports' && (
            <ReportsQueue users={users} onUserSuspended={(uid, until) => {
              setUsers(prev => prev.map(u => u.id === uid ? { ...u, is_suspended: true, suspended_until: until } : u));
            }} />
          )}

          {/* ======== PAYOUTS TAB ======== */}
          {activeTab === 'payouts' && (
            <div data-testid="admin-payouts">
              <div className="flex items-center justify-between gap-4 p-4 mb-6 rounded-arch border border-white/20 bg-white/40 dark:bg-white/10" data-testid="mentor-signup-toggle-card">
                <div>
                  <p className="font-medium text-text-main">Mentor Messaging Signup</p>
                  <p className="text-xs text-text-muted mt-0.5">
                    When off, members see "Coming soon" and can't buy the add-on or send mentor questions. Mentors and existing subscribers can still cancel or reply.
                  </p>
                  {mentorToggleError && <p className="text-xs text-red-500 mt-1" role="alert">{mentorToggleError}</p>}
                </div>
                <button
                  role="switch"
                  aria-checked={!!mentorSignupEnabled}
                  aria-label={mentorSignupEnabled ? 'Turn mentor messaging signup off' : 'Turn mentor messaging signup on'}
                  disabled={mentorToggling || mentorSignupEnabled === null}
                  onClick={handleToggleMentorSignup}
                  className="flex items-center gap-2 px-4 py-2 rounded-arch text-sm font-medium transition-colors disabled:opacity-50 shrink-0 min-h-[44px]"
                  style={{ background: mentorSignupEnabled ? 'var(--primary)' : 'rgba(0,0,0,0.1)', color: mentorSignupEnabled ? 'var(--primary-foreground)' : 'var(--text-muted)' }}
                  data-testid="mentor-signup-toggle"
                >
                  {mentorSignupEnabled === null
                    ? <><Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Loading</>
                    : mentorSignupEnabled
                      ? <><ToggleRight className="w-5 h-5" aria-hidden="true" /> On</>
                      : <><ToggleLeft className="w-5 h-5" aria-hidden="true" /> Off</>}
                </button>
              </div>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-lg font-semibold text-text-main">Mentor Payout Report</h2>
                  <p className="text-xs text-text-muted mt-0.5">$2.00 per question answered · 2-question cap per mentee/month</p>
                </div>
                <div className="flex items-center gap-3">
                  <label htmlFor="payout-month" className="text-sm text-text-muted sr-only">Select month</label>
                  <input
                    id="payout-month"
                    type="month"
                    value={payoutMonth}
                    onChange={e => setPayoutMonth(e.target.value)}
                    className="px-3 py-2 rounded-arch text-sm border focus:ring-2 focus:ring-primary"
                    style={{ background: 'rgba(255,255,255,0.7)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
                    data-testid="payout-month-picker"
                  />
                </div>
              </div>

              {payoutsLoading && (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" aria-hidden="true" />
                </div>
              )}

              {payoutsError && (
                <p className="text-sm text-red-600 py-4" role="alert">{payoutsError}</p>
              )}

              {payouts && !payoutsLoading && (
                <>
                  {/* Grand total */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="rounded-arch-lg p-4 text-center" style={{ background: 'rgba(74,173,168,0.08)', border: '1px solid rgba(74,173,168,0.2)' }}>
                      <p className="text-2xl font-semibold text-primary">{payouts.grand_total_questions_answered}</p>
                      <p className="text-xs text-text-muted mt-1">Total questions answered</p>
                    </div>
                    <div className="rounded-arch-lg p-4 text-center" style={{ background: 'rgba(146,104,10,0.08)', border: '1px solid rgba(146,104,10,0.2)' }}>
                      <p className="text-2xl font-semibold" style={{ color: '#92680a' }}>${payouts.grand_total_payout.toFixed(2)}</p>
                      <p className="text-xs text-text-muted mt-1">Total payout owed</p>
                    </div>
                  </div>

                  {/* Per-mentor table */}
                  {payouts.mentors.length === 0 ? (
                    <p className="text-sm text-text-muted text-center py-8">No mentor activity for {payouts.month}.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-arch-lg" style={{ border: '1px solid var(--border)' }}>
                      <table className="w-full text-sm" role="table" aria-label="Mentor payout breakdown">
                        <thead>
                          <tr style={{ background: 'var(--background)', borderBottom: '1px solid var(--border)' }}>
                            <th className="text-left px-4 py-3 font-medium text-text-muted text-xs">Mentor</th>
                            <th className="text-left px-4 py-3 font-medium text-text-muted text-xs">Email</th>
                            <th className="text-center px-4 py-3 font-medium text-text-muted text-xs">Questions</th>
                            <th className="text-right px-4 py-3 font-medium text-text-muted text-xs">Payout</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.mentors.map((row, i) => (
                            <tr
                              key={row.mentor_id}
                              className="border-t"
                              style={{ borderColor: 'var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.03)' }}
                              data-testid={`payout-row-${row.mentor_id}`}
                            >
                              <td className="px-4 py-3 font-medium text-text-main">{row.name}</td>
                              <td className="px-4 py-3 text-text-muted">{row.email}</td>
                              <td className="px-4 py-3 text-center text-text-main">{row.questions_answered}</td>
                              <td className="px-4 py-3 text-right font-semibold" style={{ color: row.payout > 0 ? '#92680a' : 'var(--text-muted)' }}>
                                ${row.payout.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ borderTop: '2px solid var(--border)', background: 'rgba(74,173,168,0.04)' }}>
                            <td className="px-4 py-3 font-semibold text-text-main" colSpan={2}>Total</td>
                            <td className="px-4 py-3 text-center font-semibold text-text-main">{payouts.grand_total_questions_answered}</td>
                            <td className="px-4 py-3 text-right font-semibold text-primary">${payouts.grand_total_payout.toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ======== BROADCAST TAB ======== */}
          {activeTab === 'broadcast' && (
            <div className="max-w-xl">
              <h3 className="font-heading text-lg font-semibold text-text-main mb-1">Send Community Alert</h3>
              <p className="text-sm text-text-muted mb-6">
                This message will appear in the notification bell of every community member.
              </p>
              <div className="space-y-4">
                <div>
                  <label htmlFor="broadcast-msg" className="block text-sm font-medium text-text-main mb-2">
                    Message
                  </label>
                  <textarea
                    id="broadcast-msg"
                    value={broadcastMsg}
                    onChange={(e) => setBroadcastMsg(e.target.value.slice(0, 300))}
                    rows={4}
                    placeholder="e.g. Join us for a live session this Saturday at 7PM EST — link in bio!"
                    className="w-full px-4 py-3 rounded-arch text-sm resize-none focus:ring-2 focus:ring-primary transition-all"
                    style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                    data-testid="broadcast-input"
                  />
                  <p className="text-xs text-text-muted mt-1 text-right">{broadcastMsg.length}/300</p>
                </div>

                {broadcastResult && (
                  <p
                    className={`text-sm font-medium px-4 py-3 rounded-arch ${broadcastResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}
                    role="alert"
                    data-testid="broadcast-result"
                  >
                    {broadcastResult.text}
                  </p>
                )}

                <button
                  onClick={handleBroadcast}
                  disabled={broadcastSending || !broadcastMsg.trim()}
                  className="flex items-center gap-2 px-6 py-3 rounded-arch font-medium text-sm transition-colors disabled:opacity-50"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                  data-testid="broadcast-send-btn"
                >
                  {broadcastSending
                    ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Sending...</>
                    : <><Send className="w-4 h-4" aria-hidden="true" /> Send to All Members</>}
                </button>
              </div>
            </div>
          )}
          {/* ======== SEED POSTS TAB ======== */}
          {activeTab === 'seedposts' && (
            <div>
              {/* AI toggle */}
              <div className="flex items-center justify-between p-4 mb-6 rounded-arch border border-white/20 bg-white/40 dark:bg-white/10">
                <div>
                  <p className="font-medium text-text-main">AI Auto-Posting</p>
                  <p className="text-xs text-text-muted mt-0.5">When on, test accounts automatically generate and post AI content every 30 min.</p>
                </div>
                <button
                  aria-label={seedAiEnabled ? 'Disable AI auto-posting' : 'Enable AI auto-posting'}
                  disabled={seedAiToggling}
                  onClick={async () => {
                    setSeedAiToggling(true);
                    try {
                      const res = await api.setSeedAiEnabled(!seedAiEnabled);
                      setSeedAiEnabled(res.data.enabled);
                    } catch { } finally { setSeedAiToggling(false); }
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-arch text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ background: seedAiEnabled ? 'var(--primary)' : 'rgba(0,0,0,0.1)', color: seedAiEnabled ? 'var(--primary-foreground)' : 'var(--text-muted)' }}
                >
                  {seedAiEnabled
                    ? <><ToggleRight className="w-5 h-5" aria-hidden="true" /> On</>
                    : <><ToggleLeft className="w-5 h-5" aria-hidden="true" /> Off</>}
                </button>
              </div>

              {/* Schedule new posts */}
              <div className="mb-8">
                <h3 className="font-heading text-base font-semibold text-text-main mb-1">Schedule Posts</h3>
                <p className="text-xs text-text-muted mb-4">Times are Eastern. You can schedule as yourself and as the moderator, up to {seedCap} waiting posts each. Posts publish within about 30 minutes of the time you pick.</p>
                {seedMsg.text && (
                  <p className={`text-sm mb-4 ${seedMsg.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{seedMsg.text}</p>
                )}
                <div className="space-y-3">
                  {seedRows.map((row, i) => (
                    <div key={i} className="grid grid-cols-[1fr_2fr_1fr_auto] gap-3 items-start">
                      <div>
                        <label htmlFor={`seed-account-${i}`} className="block text-xs text-text-muted mb-1">Account</label>
                        <select
                          id={`seed-account-${i}`}
                          value={row.user_id}
                          onChange={e => { const r = [...seedRows]; r[i].user_id = e.target.value; setSeedRows(r); }}
                          className="w-full px-3 py-2 rounded-arch text-sm border border-white/20 bg-white/70 dark:bg-white/10 text-text-main"
                        >
                          <option value="">Select account</option>
                          {seedAccounts.map(u => (
                            <option key={u.id} value={u.id}>{u.display_name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`seed-content-${i}`} className="block text-xs text-text-muted mb-1">Post content</label>
                        <textarea
                          id={`seed-content-${i}`}
                          rows={3}
                          value={row.content}
                          onChange={e => { const r = [...seedRows]; r[i].content = e.target.value; setSeedRows(r); }}
                          placeholder="Write the post..."
                          className="w-full px-3 py-2 rounded-arch text-sm border border-white/20 bg-white/70 dark:bg-white/10 text-text-main resize-none"
                        />

                        {/* Image upload */}
                        {row.imagePreview ? (
                          <div className="relative mt-2 inline-block">
                            <img src={row.imagePreview} alt="Upload preview" className="max-h-32 rounded-arch border border-primary/20 object-contain" />
                            <button
                              type="button"
                              aria-label="Remove image"
                              onClick={() => {
                                const r = [...seedRows];
                                r[i].imageFile = null;
                                r[i].imagePreview = null;
                                setSeedRows(r);
                              }}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full shadow"
                            >
                              <X className="w-3.5 h-3.5" aria-hidden="true" />
                            </button>
                          </div>
                        ) : (
                          <label
                            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-arch text-xs text-text-muted hover:text-primary hover:bg-primary/10 border border-white/20 cursor-pointer transition-colors"
                          >
                            <ImagePlus className="w-3.5 h-3.5" aria-hidden="true" />
                            Add image
                            <input
                              type="file"
                              accept="image/jpeg,image/png"
                              className="hidden"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (!['image/jpeg', 'image/png'].includes(file.type)) {
                                  setSeedMsg({ type: 'error', text: 'Only JPEG and PNG images are allowed.' });
                                  return;
                                }
                                if (file.size > MAX_SEED_IMAGE_SIZE) {
                                  setSeedMsg({ type: 'error', text: 'Image must be under 2MB.' });
                                  return;
                                }
                                const reader = new FileReader();
                                reader.onload = ev => {
                                  const r = [...seedRows];
                                  r[i].imageFile = file;
                                  r[i].imagePreview = ev.target.result;
                                  setSeedRows(r);
                                };
                                reader.readAsDataURL(file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </div>
                      <div>
                        <label htmlFor={`seed-time-${i}`} className="block text-xs text-text-muted mb-1">Scheduled for (Eastern time)</label>
                        <input
                          id={`seed-time-${i}`}
                          type="datetime-local"
                          value={row.scheduled_for}
                          onChange={e => { const r = [...seedRows]; r[i].scheduled_for = e.target.value; setSeedRows(r); }}
                          className="w-full px-3 py-2 rounded-arch text-sm border border-white/20 bg-white/70 dark:bg-white/10 text-text-main"
                        />
                      </div>
                      <div className="pt-6">
                        {seedRows.length > 1 && (
                          <button
                            aria-label="Remove row"
                            onClick={() => setSeedRows(seedRows.filter((_, idx) => idx !== i))}
                            className="p-2 rounded-arch text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <X className="w-4 h-4" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setSeedRows([...seedRows, { user_id: '', content: '', scheduled_for: '', imageFile: null, imagePreview: null }])}
                    className="flex items-center gap-2 px-4 py-2 rounded-arch text-sm border border-white/20 text-text-muted hover:text-text-main transition-colors"
                  >
                    <Plus className="w-4 h-4" aria-hidden="true" /> Add row
                  </button>
                  <button
                    disabled={seedSubmitting}
                    onClick={async () => {
                      const valid = seedRows.every(r => r.user_id && r.content.trim() && r.scheduled_for);
                      if (!valid) { setSeedMsg({ type: 'error', text: 'Fill in all fields for every row.' }); return; }
                      setSeedSubmitting(true);
                      setSeedMsg({ type: '', text: '' });
                      try {
                        const posts = [];
                        for (const r of seedRows) {
                          let image_url = null;
                          if (r.imageFile) {
                            const formData = new FormData();
                            formData.append('file', r.imageFile);
                            const uploadRes = await api.uploadPostImage(formData);
                            image_url = uploadRes.data.url;
                          }
                          posts.push({
                            user_id: r.user_id,
                            content: r.content.trim(),
                            scheduled_for: r.scheduled_for, // sent as typed; the server reads it as Eastern time
                            image_url,
                          });
                        }
                        await api.bulkCreateSeedPosts(posts);
                        setSeedMsg({ type: 'success', text: `${posts.length} post(s) scheduled.` });
                        setSeedRows([{ user_id: '', content: '', scheduled_for: '', imageFile: null, imagePreview: null }]);
                        const qRes = await api.getSeedQueue();
                        setSeedQueue(qRes.data || []);
                      } catch (err) {
                        setSeedMsg({ type: 'error', text: err?.response?.data?.detail || 'Failed to schedule posts.' });
                      } finally { setSeedSubmitting(false); }
                    }}
                    className="flex items-center gap-2 px-6 py-2 rounded-arch text-sm font-medium transition-colors disabled:opacity-50"
                    style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                  >
                    {seedSubmitting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Send className="w-4 h-4" aria-hidden="true" />}
                    Schedule all
                  </button>
                </div>
              </div>

              {/* Queue list */}
              <div>
                <div className="flex gap-4 mb-4 border-b border-white/20">
                  {['pending', 'published'].map(t => (
                    <button key={t} onClick={() => setSeedQueueTab(t)}
                      className={`pb-2 text-sm font-medium capitalize transition-colors ${seedQueueTab === t ? 'text-primary border-b-2 border-primary' : 'text-text-muted'}`}>
                      {t}
                    </button>
                  ))}
                </div>
                {seedQueueLoading ? (
                  <p className="text-sm text-text-muted">Loading...</p>
                ) : (
                  <div className="space-y-3">
                    {seedQueue
                      .filter(p => seedQueueTab === 'pending' ? !p.posted : p.posted)
                      .map(p => (
                        <div key={p.id} className="flex items-start gap-4 p-4 rounded-arch border border-white/20 bg-white/40 dark:bg-white/10">
                          {p.image_url && (
                            <img src={p.image_url} alt="" className="w-14 h-14 rounded-arch object-cover flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-text-muted mb-1">
                              <span className="font-medium text-text-main">{p.users?.display_name || p.user_id}</span>
                              {' · '}
                              {p.posted
                                ? `Published ${new Date(p.posted_at).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' })} ET`
                                : `Scheduled ${new Date(p.scheduled_for).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' })} ET`}
                            </p>
                            <p className="text-sm text-text-main">{p.content}</p>
                          </div>
                          {!p.posted && (
                            <button
                              aria-label="Delete scheduled post"
                              onClick={async () => {
                                try {
                                  await api.deleteSeedPost(p.id);
                                  setSeedQueue(q => q.filter(x => x.id !== p.id));
                                } catch { }
                              }}
                              className="p-2 rounded-arch text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      ))}
                    {seedQueue.filter(p => seedQueueTab === 'pending' ? !p.posted : p.posted).length === 0 && (
                      <p className="text-sm text-text-muted">No {seedQueueTab} posts.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ======== ISLAMOJIS TAB ======== */}
          {activeTab === 'islamojis' && (
            <div>
              <h3 className="font-heading text-base font-semibold text-text-main mb-4 flex items-center gap-2">
                <Moon className="w-5 h-5 text-primary" aria-hidden="true" /> Islamojis
              </h3>
              {islamojiMsg.text && (
                <p className={`text-sm mb-4 ${islamojiMsg.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{islamojiMsg.text}</p>
              )}

              {/* Upload form */}
              <div className="mb-8 p-4 rounded-arch-lg border border-primary/15" style={{ background: 'rgba(74,173,168,0.05)' }}>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
                  <div>
                    <label htmlFor="islamoji-name" className="block text-xs text-text-muted mb-1">Name</label>
                    <input
                      id="islamoji-name"
                      type="text"
                      value={islamojiName}
                      onChange={e => setIslamojiName(e.target.value)}
                      placeholder="cute-cat"
                      className="w-full px-3 py-2 rounded-arch text-sm border border-white/20 bg-white/70 dark:bg-white/10 text-text-main"
                    />
                  </div>
                  <div>
                    <label htmlFor="islamoji-type" className="block text-xs text-text-muted mb-1">Type</label>
                    <select
                      id="islamoji-type"
                      value={islamojiType}
                      onChange={e => setIslamojiType(e.target.value)}
                      className="w-full px-3 py-2 rounded-arch text-sm border border-white/20 bg-white/70 dark:bg-white/10 text-text-main"
                    >
                      <option value="emoji">Emoji</option>
                      <option value="gif">GIF</option>
                    </select>
                  </div>
                  {islamojiType === 'emoji' && (
                    <div>
                      <label htmlFor="islamoji-category" className="block text-xs text-text-muted mb-1">Category</label>
                      <select
                        id="islamoji-category"
                        value={islamojiCategory}
                        onChange={e => setIslamojiCategory(e.target.value)}
                        className="w-full px-3 py-2 rounded-arch text-sm border border-white/20 bg-white/70 dark:bg-white/10 text-text-main capitalize"
                      >
                        {ISLAMOJI_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label htmlFor="islamoji-sort-order" className="block text-xs text-text-muted mb-1">Sort order</label>
                    <input
                      id="islamoji-sort-order"
                      type="number"
                      value={islamojiSortOrder}
                      onChange={e => setIslamojiSortOrder(e.target.value)}
                      placeholder="0"
                      className="w-full px-3 py-2 rounded-arch text-sm border border-white/20 bg-white/70 dark:bg-white/10 text-text-main"
                    />
                  </div>
                  <div>
                    <label htmlFor="islamoji-file" className="block text-xs text-text-muted mb-1">File</label>
                    <input
                      id="islamoji-file"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={e => setIslamojiFile(e.target.files?.[0] || null)}
                      className="w-full text-xs text-text-muted"
                    />
                  </div>
                </div>
                <button
                  disabled={islamojiUploading}
                  onClick={handleIslamojiUpload}
                  className="mt-4 flex items-center gap-2 px-6 py-2 rounded-arch text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  {islamojiUploading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Plus className="w-4 h-4" aria-hidden="true" />}
                  Add Islamoji
                </button>
                <p className="text-[11px] text-text-muted mt-2">
                  Lower sort order shows first. To keep a set together within a category (e.g. sisters vs. brothers in People), give them the same number — e.g. sisters = 1, brothers = 2.
                </p>
              </div>

              {/* Browse existing */}
              {islamojiLoading ? (
                <p className="text-sm text-text-muted">Loading...</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {ISLAMOJI_CATEGORIES.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setIslamojiFilterTab(cat)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${islamojiFilterTab === cat ? 'bg-primary text-primary-foreground' : 'bg-white/60 dark:bg-white/10 text-text-muted hover:text-primary'}`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-6 sm:grid-cols-10 gap-2 mb-8">
                    {islamojiEmojisInTab.length === 0 ? (
                      <p className="col-span-full text-sm text-text-muted">No emojis in this category yet.</p>
                    ) : islamojiEmojisInTab.map(item => (
                      <div key={item.id} className="relative group aspect-square rounded-arch overflow-hidden border border-primary/15">
                        <img src={item.image_url} alt={item.name} title={item.name} className="w-full h-full object-cover" />
                        <button
                          aria-label={`Delete ${item.name}`}
                          onClick={() => handleIslamojiDelete(item.id)}
                          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4 text-white" aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <h4 className="font-heading text-sm font-semibold text-text-main mb-3">GIFs</h4>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                    {islamojiGifs.length === 0 ? (
                      <p className="col-span-full text-sm text-text-muted">No GIFs yet.</p>
                    ) : islamojiGifs.map(item => (
                      <div key={item.id} className="relative group aspect-square rounded-arch overflow-hidden border border-primary/15">
                        <img src={item.image_url} alt={item.name} title={item.name} className="w-full h-full object-cover" />
                        <button
                          aria-label={`Delete ${item.name}`}
                          onClick={() => handleIslamojiDelete(item.id)}
                          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4 text-white" aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AdminPage;
